import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { QueryError } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';
import queryString from 'query-string';

import { api } from 'app/app.api';

import {
  CreateAiFeedbackApiArg,
  CreateAiTextTranslationApiArg,
  CreateAiTextTranslationApiResponse,
  CreateAiWritingImprovementArg,
  CreateAiWritingImprovementResponse,
  CreateAiWritingStyleGuideArg,
  CreateAIWritingStyleGuidesResponse,
  DeleteAiWritingStyleGuideArg,
  ReadAiCheckGrammarAndSpellingApiArg,
  ReadAiCheckGrammarAndSpellingApiResponse,
  ReadAiStyleGuideMatchApiResponse,
  ReadAiTextTranslationApiArg,
  ReadAiTextTranslationApiResponse,
  ReadAiWritingGuideMatchApiArg,
  ReadAiWritingImprovementArg,
  ReadAiWritingImprovementResponse,
  ReadAIWritingStyleGuideArg,
  ReadAIWritingStyleGuideResponse,
  ReadAIWritingStyleGuidesResponse,
} from './types/aiApi.types';
import { AiStyleGuide } from './types/aiStyleGuides.types';
import { streamUpdates } from './utils/streamUpdates';

const addTraceStateHeader = (csid: string | undefined) => {
  if (!csid) {
    return {};
  }
  return {
    headers: {
      tracestate: `csid=${csid}`,
    },
  };
};

export const aiApi = api
  .enhanceEndpoints({
    addTagTypes: ['WritingStyleGuides', 'WritingStyleGuidesMatch'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readAIWritingStyleGuideMatch: builder.query<
        ReadAiStyleGuideMatchApiResponse,
        ReadAiWritingGuideMatchApiArg
      >({
        query: ({ features, channels, csid }) => {
          const channelSnaked = channels
            ? (convertChannelCasing(
                channels,
                'snake',
              ) as AiStyleGuide['contexts']['channels'][number])
            : channels;
          return {
            version: 4,
            path: 'ai/style-guide/best_match',
            method: 'POST',
            body: {
              features,
              channels: channelSnaked,
            },
            ...addTraceStateHeader(csid),
          };
        },
        providesTags: ['WritingStyleGuidesMatch'],
      }),
      createAiTextTranslation: builder.mutation<
        CreateAiTextTranslationApiResponse,
        CreateAiTextTranslationApiArg
      >({
        query: ({
          language,
          text,
          csid,
          contentType,
          direction = 'inbound',
        }) => {
          const path =
            direction === 'inbound'
              ? 'ai/text/translate-v2'
              : 'ai/text/translate-v2-outbound';
          return {
            version: 4,
            path: `${path}?${queryString.stringify({
              use_cache: true,
            })}`,
            method: 'POST',
            body: {
              language,
              text,
              ...(contentType && { content_type: contentType }),
            },
            timeout: 20000,
            ...addTraceStateHeader(csid),
          };
        },
      }),
      readStreamAiTextTranslation: builder.query<
        ReadAiTextTranslationApiResponse,
        ReadAiTextTranslationApiArg
      >({
        queryFn: () => {
          return {
            data: {
              text: '',
              meta: {
                isLoading: true,
                isStreaming: false,
                isError: false,
                isSuccess: false,
              },
            },
          } as MaybePromise<
            QueryReturnValue<ReadAiTextTranslationApiResponse, QueryError>
          >;
        },
        async onQueryStarted(arg, { updateCachedData }) {
          const { text, language, contentType, direction } = arg;
          const path =
            direction === 'inbound'
              ? 'ai/text/translate-v2'
              : 'ai/text/translate-v2-outbound';

          const requestConfig = {
            url: `${
              config.url.rest
            }/v4/${path}?stream=true&${queryString.stringify({
              use_cache: true,
            })}`,
            body: {
              text,
              language,
              ...(contentType && { content_type: contentType }),
            },
            ...addTraceStateHeader(arg.csid),
          };
          await streamUpdates(updateCachedData, requestConfig);
        },
      }),
      createAiFeedback: builder.mutation<void, CreateAiFeedbackApiArg>({
        query: ({ feedbackId, feedback: body }) => ({
          version: 4,
          path: `ai/feedback/${feedbackId}`,
          method: 'POST',
          body,
        }),
      }),
      createAiWritingImprovement: builder.mutation<
        CreateAiWritingImprovementResponse,
        CreateAiWritingImprovementArg
      >({
        query: ({
          promptName,
          text,
          lastMessage = '',
          instructions,
          contexts,
          csid,
        }) => ({
          version: 5,
          path: `ai/text/${promptName}`,
          method: 'POST',
          body: {
            metadata: {
              'style-guide-context': contexts,
            },
            inputs: {
              text,
              last_message: lastMessage,
              ...(instructions && { instructions }),
            },
          },
          ...addTraceStateHeader(csid),
        }),
      }),
      /**
       * This query is not used as regular query. It consist of two parts, the
       * queryFn and the onQueryStarted function.
       *
       * QueryFn acts as a placeholder data object which by passes the normal
       * behavior of RTK-Q which would be to actually make an HTTP request.
       * Instead we set placeholder data, which we can then update later.
       *
       * OnQueryStarted is a RTK-Q lifecycle hook which is called on each query
       * trigger. Because the query itself doesn't actually do anything, the
       * entire logic of fetching the data is done through this hook. We call an
       * HTTP streaming API, and manually update the cached data. The cached
       * data consists of the text returned as a result from the streaming api,
       * but also within the function, we set the server state such as isLoading
       * if the query is loading and isStreaming in case we have started to
       * fetch some tokens.
       */
      readStreamAiCheckGrammarAndSpelling: builder.query<
        ReadAiCheckGrammarAndSpellingApiResponse,
        ReadAiCheckGrammarAndSpellingApiArg
      >({
        queryFn: () => {
          return {
            data: {
              text: '',
              meta: {
                isLoading: true,
                isStreaming: false,
                isError: false,
                isSuccess: false,
              },
            },
          } as MaybePromise<
            QueryReturnValue<
              ReadAiCheckGrammarAndSpellingApiResponse,
              QueryError
            >
          >;
        },
        async onQueryStarted(arg, { updateCachedData }) {
          const { text, csid } = arg;
          const requestConfig = {
            url: `${config.url.rest}/v4/ai/text/grammar-and-spell-check?stream=true`,
            body: {
              text,
            },
            ...addTraceStateHeader(csid),
          };
          await streamUpdates(updateCachedData, requestConfig);
        },
      }),
      // Non-streaming version of readStreamAiCheckGrammarAndSpelling
      readAiCheckGrammarAndSpelling: builder.query<
        ReadAiCheckGrammarAndSpellingApiResponse,
        ReadAiCheckGrammarAndSpellingApiArg
      >({
        query: ({ text, csid }) => ({
          version: 4,
          path: 'ai/text/grammar-and-spell-check',
          method: 'POST',
          body: {
            text,
          },
          ...addTraceStateHeader(csid),
        }),
      }),

      // Behaves the same as readStreamAiCheckGrammarAndSpelling endpoint above
      readStreamAiWritingImprovement: builder.query<
        ReadAiWritingImprovementResponse,
        ReadAiWritingImprovementArg
      >({
        queryFn: () => {
          return {
            data: {
              text: '',
              meta: {
                isLoading: true,
                isStreaming: false,
                isError: false,
                isSuccess: false,
              },
              feedback_id: '',
              total_tokens: 0,
            },
          } as MaybePromise<
            QueryReturnValue<ReadAiWritingImprovementResponse, QueryError>
          >;
        },
        async onQueryStarted(arg, { updateCachedData }) {
          const {
            promptName,
            text,
            lastMessage,
            instructions,
            contexts,
            csid,
          } = arg;

          const requestConfig = {
            url: `${config.url.rest}/v5/ai/text/${promptName}?stream=true`,
            body: {
              metadata: {
                'style-guide-context': contexts,
              },
              inputs: {
                text,
                last_message: lastMessage,
                ...(instructions && { instructions }),
              },
            },
            ...addTraceStateHeader(csid),
          };

          await streamUpdates(updateCachedData, requestConfig);
        },
      }),
      createAIWritingStyleGuide: builder.mutation<
        CreateAIWritingStyleGuidesResponse,
        CreateAiWritingStyleGuideArg
      >({
        query: (styleGuide) => ({
          version: 4,
          path: 'ai/style-guide/',
          method: 'POST',
          body: styleGuide,
        }),
        invalidatesTags: ['WritingStyleGuides', 'WritingStyleGuidesMatch'],
      }),
      readAIWritingStyleGuides: builder.query<
        ReadAIWritingStyleGuidesResponse,
        void
      >({
        query: () => ({
          version: 4,
          path: 'ai/style-guide/',
        }),
        providesTags: ['WritingStyleGuides'],
      }),
      readAIWritingStyleGuide: builder.query<
        ReadAIWritingStyleGuideResponse,
        ReadAIWritingStyleGuideArg
      >({
        query: ({ styleguideId }) => ({
          version: 4,
          path: `ai/style-guide/${styleguideId}`,
        }),
      }),
      updateAIWritingStyleGuide: builder.mutation({
        query: ({ styleguideId, ...rest }) => ({
          version: 4,
          path: `ai/style-guide/${styleguideId}`,
          method: 'PUT',
          body: rest,
        }),
        invalidatesTags: ['WritingStyleGuides', 'WritingStyleGuidesMatch'],
      }),
      deleteAIWritingStyleGuide: builder.mutation<
        void,
        DeleteAiWritingStyleGuideArg
      >({
        query: ({ styleguideId }) => ({
          version: 4,
          path: `ai/style-guide/${styleguideId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['WritingStyleGuides', 'WritingStyleGuidesMatch'],
      }),
    }),
  });
